import axios from "axios";
import { logToServer } from "./Logger";

//Format the image once and return both blob URL (for preview) and data URL (for S3 upload)
export function formatImage(ev: any, imageMaxWidth: number): Promise<{ blobUrl: string; dataUrl: string }> {
  return new Promise((resolve, reject) => {
    const imgElement = document.createElement("img");
    imgElement.src = ev.target!.result!.toString();

    imgElement.onload = (e: Event) => {
      const canvas = document.createElement("canvas");
      const scaleSize =
        imageMaxWidth / (e.currentTarget as HTMLImageElement).width;
      var context = canvas.getContext("2d");

      //set width and height based on env variables and calculated scale
      canvas.width = imageMaxWidth;
      canvas.height = (e.currentTarget as HTMLImageElement).height * scaleSize;

      //Draw the image once
      context!.drawImage(imgElement, 0, 0, canvas.width, canvas.height);

      // Get data URL for S3 upload
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);

      // Convert canvas to blob and create blob URL for preview (CSP-friendly)
      canvas.toBlob((blob) => {
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          resolve({ blobUrl, dataUrl });
        } else {
          reject(new Error("Failed to create blob"));
        }
      }, "image/jpeg", 0.85);
    };
  });
}

//Format the image to generate both main and large image versions
export function formatImageWithLarge(ev: any, imageMaxWidth: number, largeImageMaxWidth: number): Promise<{ blobUrl: string; dataUrl: string; largeDataUrl: string }> {
  return new Promise((resolve, reject) => {
    const imgElement = document.createElement("img");
    imgElement.src = ev.target!.result!.toString();

    imgElement.onload = (e: Event) => {
      const imageElement = e.currentTarget as HTMLImageElement;

      // Create main image
      const mainCanvas = document.createElement("canvas");
      const mainScaleSize = imageMaxWidth / imageElement.width;
      const mainContext = mainCanvas.getContext("2d");

      // Enable high-quality image smoothing for main image
      mainContext!.imageSmoothingEnabled = true;
      mainContext!.imageSmoothingQuality = "high";

      mainCanvas.width = imageMaxWidth;
      mainCanvas.height = imageElement.height * mainScaleSize;
      mainContext!.drawImage(imgElement, 0, 0, mainCanvas.width, mainCanvas.height);

      const dataUrl = mainCanvas.toDataURL("image/jpeg", 0.85);

      // Create large image with higher quality settings
      const largeCanvas = document.createElement("canvas");
      const largeScaleSize = Math.min(largeImageMaxWidth / imageElement.width, 1); // Don't upscale
      const largeContext = largeCanvas.getContext("2d");

      // Enable high-quality image smoothing for large image
      largeContext!.imageSmoothingEnabled = true;
      largeContext!.imageSmoothingQuality = "high";

      largeCanvas.width = imageElement.width * largeScaleSize;
      largeCanvas.height = imageElement.height * largeScaleSize;
      largeContext!.drawImage(imgElement, 0, 0, largeCanvas.width, largeCanvas.height);

      const largeDataUrl = largeCanvas.toDataURL("image/jpeg", 0.85); // High quality for large image, tuned to balance file size vs. text legibility

      // Convert main canvas to blob and create blob URL for preview (CSP-friendly)
      mainCanvas.toBlob((blob) => {
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          resolve({ blobUrl, dataUrl, largeDataUrl });
        } else {
          reject(new Error("Failed to create blob"));
        }
      }, "image/jpeg", 0.85);
    };
  });
}

//Legacy function - kept for backward compatibility, but prefer using formatImage()
export function formatImageForS3(ev: any, imageMaxWidth: number) {
  return new Promise((resolve, reject) => {
    const imgElement = document.createElement("img");
    imgElement.src = ev.target!.result!.toString();

    imgElement.onload = (e: Event) => {
      const canvas = document.createElement("canvas");
      const scaleSize =
        imageMaxWidth / (e.currentTarget as HTMLImageElement).width;
      var context = canvas.getContext("2d");

      //set width and height based on env variables and calculated scale
      canvas.width = imageMaxWidth;
      canvas.height = (e.currentTarget as HTMLImageElement).height * scaleSize;

      //Draw the image
      context!.drawImage(imgElement, 0, 0, canvas.width, canvas.height);
      resolve(context!.canvas.toDataURL("image/jpeg", .85));
    };
  });
}

//Convert file into URL since S3 requires a file not a URL
export function dataURLtoFile(dataurl: string, filename: string): File | null {
  const arr = dataurl.split(",");
  if (arr.length < 2) return null;

  const mimeMatch = arr[0].match(/:(.*?);/);
  if (!mimeMatch) return null;

  const mime = mimeMatch[1];
  const bstr = atob(arr[1]);
  const u8arr = new Uint8Array(bstr.length);

  for (let i = 0; i < bstr.length; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }

  return new File([u8arr], filename, { type: mime });
}

export async function saveImageToS3(
  S3Url: string,
  fileUrl: string,
  uuid: string,
  filename: string,
  userId: string,
  userContext: {},
  authToken: string,
  timeoutMs: number = 4000
): Promise<number> {
  const path = `user_images/${userId}/${uuid}_${filename}`;
  const img = dataURLtoFile(fileUrl, path);
  if (!img) {
    logToServer({
      level: "error",
      message: `"Unable to convert image to a file - "${userId}`,
      context: userContext,
      authToken,
    });
    return 404;
  }

  try {
    const res = await axios.put(S3Url, img, {
      headers: {
        "Content-Type": "image/jpeg",
        "Cache-Control": "max-age=31536000",
      },
      timeout: timeoutMs,
    });
    return res.status;
  } catch (error: any) {
    if (error.code === "ECONNABORTED") {
      logToServer({
        level: "error",
        message: `"S3 push failed due to timeout - "${userId}`,
        context: userContext,
        authToken,
      });
      return 408;
    }
    if (axios.isAxiosError(error)) {
      logToServer({
        level: "error",
        message: `"Axios Error Returned - "${userId}`,
        context: userContext,
        authToken,
      });
      return error.response?.status || 500;
    }
    logToServer({
      level: "error",
      message: `"Unknown error during S3 upload - "${userId}`,
      context: userContext,
      authToken,
    });
    return 500;
  }
}
